import { useEffect } from 'react';
import GlobalStyle from './styles/GlobalStyles';
import Router from './navigation/Router';
import { Provider as AlertProvider, positions, transitions } from 'react-alert';
import AlertTemplate from 'react-alert-template-basic';
import { useSelector } from 'react-redux';
import { ENVIRONMENTS, EventHelperTypes } from './services/exports/Constants';
import HelperMethods from './services/exports/HelperMethods';
import MetaInfo from './components-library/MetaInfo';
import ErrorScreen from './pages/ErrorScreen';
import { Libraries, useLoadScript } from '@react-google-maps/api';
import useManifest from './hooks/seo/useManifest';
import ChannelStack from './pusher-channels/ChannelStack';
import ReduxActions from './store/ReduxActions';
import { actionCreators } from './store/actions';
import CustomToaster from './components-library/CustomToaster';
import MenuProvider from './providers/MenuProvider';
import InitialDataProvider from './providers/InitialDataProvider';
import AppMethodsProvider from './providers/AppMethodsProvider';
import AlertModalProvider from './providers/AlertModalProvider';
import PusherProvider from './providers/PusherProvider';
import AppStateProvider from './providers/AppStateProvider';
import CompanySlugProvider from './providers/CompanySlugProvider';
import ScreenTypeProvider from './providers/ScreenTypeProvider';
import * as Sentry from '@sentry/react';
import BasketProvider from './providers/BasketProvider';
import { StoreInterface } from './store/types';
import LoadingProvider from './providers/LoadingProvider';
import PaymentWalletsValidator from './components-library/payment/wallet-validators/PaymentWalletsValidator';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import ProductLikesProvider from './providers/ProductLikesProvider';
import OrderFlowProvider from './providers/OrderFlowProvider';
import PaymentFlowProvider from './providers/PaymentFlowProvider';
import ThemeProvider from './providers/ThemeProvider';
import AuthProvider from './providers/AuthProvider';
import TrackingProvider from './providers/TrackingProvider';
import MobileApp from './modules/mobile/MobileApp';
import useMobileApp from './hooks/mobile-app/useMobileApp';
import PromoCodeProvider from './providers/PromoCodeProvider';
import OrderStatusProvider from './providers/OrderStatusProvider';
import AnalyticsProvider from './providers/AnalyticsProvider';
import SideEffectsProvider from './providers/SideEffectsProvider';
import MarketingProvider from './providers/MarketingProvider';

const options = {
  position: positions.MIDDLE,
  timeout: 3000,
  offset: '-30px',
  transition: transitions.SCALE,
  containerStyle: { zIndex: 5000 },
};

const libraries = ['places', 'geometry'];

function App() {
  const { event } = useSelector((state: StoreInterface) => state.eventHelper);
  const { environment } = useSelector((state: StoreInterface) => state.appStatus);
  const { company } = useSelector((store: StoreInterface) => store.initialData);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { isMobileApp } = useMobileApp();

  useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: libraries as Libraries,
  });

  useManifest();

  useEffect(() => {
    handleReduxSideEffects();
  }, [event]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      Sentry.getCurrentScope()
        .setUser({
          id: profile?.id,
          email: cached_order?.customer_email,
          type: 'consumer',
        })
        .setTag('company_id', company?.id);
    }
  }, [profile?.id, cached_order?.customer_email, company?.id]);

  async function handleReduxSideEffects() {
    const { type } = event;

    if (!type) {
      return;
    }

    return new Promise((resolve, reject) => {
      switch (type) {
        case EventHelperTypes.LOG_OUT:
          clearAllData(true);
          break;
      }
      resolve(true);
      reject(false);
    }).then(HelperMethods.resetEventSideEffects);
  }

  useEffect(() => {
    if (import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD) {
      clearAllData();
    }
  }, []);

  function clearAllData(force: boolean = false): void {
    new Promise((resolve) => {
      if (force || (environment && environment !== import.meta.env.VITE_MODE)) {
        localStorage.clear();
        ReduxActions.clearReduxStore();
        window.location.reload();
      }

      resolve(true);
    }).finally(() => {
      ReduxActions.dispatch(actionCreators.appStatus.setEnvironment(import.meta.env.VITE_MODE));
    });
  }

  return (
    <div data-test="body" className="h-full">
      <GlobalStyle />
      <Sentry.ErrorBoundary fallback={<ErrorScreen />}>
        <SideEffectsProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <ThemeProvider>
              <AuthProvider>
                <AppStateProvider>
                  <AppMethodsProvider>
                    <InitialDataProvider>
                      <AnalyticsProvider>
                        <PusherProvider>
                          <ScreenTypeProvider>
                            <TrackingProvider>
                              <MarketingProvider>
                                <AlertModalProvider>
                                  {/* @ts-expect-error Weird ts error */}
                                  <AlertProvider template={AlertTemplate} {...options}>
                                    <MenuProvider>
                                      <BasketProvider>
                                        <CompanySlugProvider>
                                          <LoadingProvider>
                                            <PaymentFlowProvider>
                                              <OrderFlowProvider>
                                                <OrderStatusProvider>
                                                  <PromoCodeProvider>
                                                    <ProductLikesProvider>
                                                      <PaymentWalletsValidator />
                                                      {isMobileApp ? <MobileApp /> : <Router />}
                                                    </ProductLikesProvider>
                                                  </PromoCodeProvider>
                                                </OrderStatusProvider>
                                              </OrderFlowProvider>
                                            </PaymentFlowProvider>
                                          </LoadingProvider>
                                          <MetaInfo />
                                          <ChannelStack />
                                        </CompanySlugProvider>
                                      </BasketProvider>
                                    </MenuProvider>
                                    <CustomToaster />
                                  </AlertProvider>
                                </AlertModalProvider>
                              </MarketingProvider>
                            </TrackingProvider>
                          </ScreenTypeProvider>
                        </PusherProvider>
                      </AnalyticsProvider>
                    </InitialDataProvider>
                  </AppMethodsProvider>
                </AppStateProvider>
              </AuthProvider>
            </ThemeProvider>
          </LocalizationProvider>
        </SideEffectsProvider>
      </Sentry.ErrorBoundary>
    </div>
  );
}

export default App;
